<template>
  <el-dialog
    title="生成计提"
    :visible.sync="dialogVisible"
    width="640px"
    :close-on-click-modal="false"
    :close-on-press-escape="!submitting"
    :show-close="!submitting"
    @close="handleClose"
  >
    <el-form ref="formRef" :model="form" :rules="dynamicRules" label-width="100px">
      <el-form-item label="岗位" prop="stationName">
        <el-select v-model="form.stationName" placeholder="请选择" style="width: 100%" @change="onStationChange">
          <el-option v-for="s in stationOptions" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
      </el-form-item>

      <el-form-item label="业务月份" prop="businessMonths">
        <el-popover
          v-model="monthPopoverVisible"
          placement="bottom-start"
          width="320"
          trigger="click"
          popper-class="business-month-popover"
        >
          <div class="month-panel">
            <div class="month-panel-header">
              <i class="el-icon-arrow-left" @click="shiftYear(-1)" />
              <span>{{ panelYear }} 年</span>
              <i class="el-icon-arrow-right" @click="shiftYear(1)" />
            </div>
            <div class="month-grid">
              <div
                v-for="m in 12"
                :key="m"
                class="month-cell"
                :class="{ selected: isSelected(panelYear, m) }"
                @click="toggleMonth(panelYear, m)"
              >
                {{ m }}月
              </div>
            </div>
            <div class="month-panel-footer">
              <span class="selected-count">已选 {{ form.businessMonths.length }} 个月</span>
              <el-button type="text" @click="form.businessMonths = []">清空</el-button>
            </div>
          </div>
          <el-input
            slot="reference"
            :value="displayText"
            readonly
            placeholder="请选择业务月份（可多选）"
            suffix-icon="el-icon-date"
            style="width: 100%; cursor: pointer"
          />
        </el-popover>
        <div class="tip">每选一个月份将生成一个独立批次</div>
      </el-form-item>

      <el-form-item label="计提月份" prop="provisionDate">
        <el-date-picker
          v-model="form.provisionDate"
          type="month"
          placeholder="选择月份"
          value-format="yyyy-MM-dd"
          style="width: 100%"
        />
      </el-form-item>

      <el-form-item v-if="isConfigurable" label="提成规则" prop="ruleUuids">
        <div v-if="rulesLoading" class="tip">正在加载岗位规则…</div>
        <el-checkbox-group v-else-if="enabledRules.length" v-model="form.ruleUuids">
          <el-checkbox v-for="r in enabledRules" :key="r.uuid" :label="r.uuid" class="rule-checkbox">
            <span class="rule-name">{{ r.ruleName }}</span>
            <span class="rule-meta">（{{ calcTypeLabel(r.calcType) }}<template v-if="shouldShowFixedAmount(r)"> {{ r.fixedAmount }}元</template>）</span>
          </el-checkbox>
        </el-checkbox-group>
        <div v-else-if="form.stationName" class="tip warn">
          该岗位暂无已启用的规则。请先在侧边栏「计提规则配置 → 岗位规则配置」中添加规则，并确认状态为「启用」。
        </div>
        <div v-if="enabledRules.length" class="tip">勾选本次生成要参与的岗位规则；人员特殊规则仍会自动叠加</div>
      </el-form-item>
    </el-form>

    <div slot="footer">
      <el-button :disabled="submitting" @click="dialogVisible = false">{{ $t('common.btn.cancel') }}</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">
        {{ submitting ? '生成中...' : $t('common.btn.confirm') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { generateProvision } from '@/api/provision/provisionData'
import { queryEnabledPositionRules } from '@/api/provision/provisionRule'
import * as ProvisionConstants from '@/class/cargo/model/parameters/ProvisionConstants'

const CONFIGURABLE_VALUES = ProvisionConstants.ALL_GENERATE_STATIONS.map(s => s.value)

export default {
  name: 'GenerateDialog',
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      stationOptions: ProvisionConstants.ALL_GENERATE_STATIONS,
      enabledRules: [],
      rulesLoading: false,
      monthPopoverVisible: false,
      panelYear: new Date().getFullYear(),
      submitting: false,
      form: {
        stationName: '',
        provisionDate: '',
        businessMonths: [],
        ruleUuids: []
      }
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    },
    isConfigurable() {
      return CONFIGURABLE_VALUES.includes(this.form.stationName)
    },
    displayText() {
      if (!this.form.businessMonths.length) return ''
      return [...this.form.businessMonths]
        .sort()
        .map(v => v.substring(0, 7))
        .join('、')
    },
    dynamicRules() {
      const rules = {
        stationName: [{ required: true, message: '请选择岗位', trigger: 'change' }],
        provisionDate: [{ required: true, message: '请选择计提月份', trigger: 'change' }],
        businessMonths: [{ type: 'array', required: true, min: 1, message: '请至少选择一个业务月份', trigger: 'change' }]
      }
      if (this.isConfigurable) {
        rules.ruleUuids = [{ type: 'array', required: true, min: 1, message: '请选择提成规则', trigger: 'change' }]
      }
      return rules
    }
  },
  watch: {
    visible(val) {
      if (val && this.form.stationName) {
        this.onStationChange()
      }
    }
  },
  methods: {
    calcTypeLabel(v) {
      return ProvisionConstants.getCalcTypeLabel(v)
    },
    shouldShowFixedAmount(rule) {
      if (!rule || (rule.calcType !== 'fixed' && rule.calcType !== 'fixed_bonus')) {
        return false
      }
      return Number(rule.fixedAmount) > 0
    },
    monthValue(year, month) {
      return `${year}-${String(month).padStart(2, '0')}-01`
    },
    isSelected(year, month) {
      return this.form.businessMonths.includes(this.monthValue(year, month))
    },
    toggleMonth(year, month) {
      const val = this.monthValue(year, month)
      const idx = this.form.businessMonths.indexOf(val)
      if (idx >= 0) this.form.businessMonths.splice(idx, 1)
      else this.form.businessMonths.push(val)
    },
    shiftYear(delta) {
      this.panelYear += delta
    },
    async onStationChange() {
      this.form.ruleUuids = []
      this.enabledRules = []
      if (!this.isConfigurable) return
      this.rulesLoading = true
      try {
        const res = await queryEnabledPositionRules(this.form.stationName)
        if (!res.ok) {
          this.$message.error('加载岗位规则失败')
          return
        }
        this.enabledRules = (res.data || []).filter(r => r.calcType !== '_config' && r.uuid)
        this.form.ruleUuids = this.enabledRules.map(r => r.uuid)
      } catch (e) {
        console.error('onStationChange failed', e)
        this.$message.error('加载岗位规则失败')
      } finally {
        this.rulesLoading = false
      }
    },
    async handleSubmit() {
      if (this.submitting) return
      if (this.isConfigurable && !this.form.ruleUuids.length) {
        this.$message.warning('请选择提成规则，或先到规则配置中添加已启用的岗位规则')
        return
      }
      try {
        await this.$refs.formRef.validate()
      } catch (e) {
        return
      }
      const businessMonths = [...this.form.businessMonths].sort()
      const payload = {
        stationName: this.form.stationName,
        provisionDate: this.form.provisionDate,
        businessMonths,
        ruleUuids: this.form.ruleUuids
      }
      this.submitting = true
      try {
        const res = await generateProvision(payload)
        if (res.ok) {
          this.$emit('success', businessMonths.length)
          this.dialogVisible = false
        }
      } catch (e) {
        console.error('生成计提失败', e)
      } finally {
        this.submitting = false
      }
    },
    handleClose() {
      this.form = { stationName: '', provisionDate: '', businessMonths: [], ruleUuids: [] }
      this.enabledRules = []
      this.rulesLoading = false
      this.submitting = false
      this.monthPopoverVisible = false
      this.panelYear = new Date().getFullYear()
    }
  }
}
</script>

<style scoped>
.tip { color: #909399; font-size: 12px; margin-top: 4px; }
.tip.warn { color: #E6A23C; }
::v-deep .el-form-item { margin-bottom: 28px !important; }
.rule-checkbox { display: block; margin-left: 0 !important; margin-bottom: 6px; }
.rule-name { font-weight: 500; }
.rule-meta { color: #909399; font-size: 12px; }

.month-panel { padding: 4px 4px 0; }
.month-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px 10px;
  font-size: 14px;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
}
.month-panel-header i {
  cursor: pointer;
  padding: 4px;
  color: #606266;
}
.month-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 12px 8px;
}
.month-cell {
  height: 36px;
  line-height: 36px;
  text-align: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #606266;
  border: 1px solid transparent;
  transition: background 0.15s;
}
.month-cell:hover { background: #f5f7fa; }
.month-cell.selected {
  background: #409EFF;
  color: #fff;
  border-color: #409EFF;
}
.month-panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-top: 1px solid #ebeef5;
}
.selected-count { color: #909399; font-size: 12px; }
</style>
